import { type ITranslationMap } from '@principle-theorem/practice-migrations/interfaces';
import {
  AtLeast,
  INamedDocument,
  IXSLXExport,
  IXSLXImport,
  XSLXCell,
  isSameRef,
} from '@principle-theorem/shared';
import { Column, Row } from 'exceljs';

export interface ISourceRecordToXSLX {
  label: string;
  mapTo: string;
}

export interface IBasicMigrationRecord {
  id: string | number;
  name: string;
}

export abstract class BaseSourceToXSLX<
  PrincipleRecord extends object,
  SourceRecord extends IBasicMigrationRecord = IBasicMigrationRecord,
> implements IXSLXExport<SourceRecord, ISourceRecordToXSLX>
{
  abstract headers: AtLeast<Column, 'key' | 'header'>[];

  constructor(
    private _sourceRecords: INamedDocument<PrincipleRecord>[],
    private _existingMappings: ITranslationMap<PrincipleRecord>[]
  ) {}

  translate(
    records: SourceRecord[]
  ): Record<keyof ISourceRecordToXSLX, XSLXCell>[] {
    return records.map((record) => {
      const existingRecord = this._existingMappings.find(
        (existingMapping) =>
          existingMapping.sourceIdentifier === record.id.toString()
      );

      const destinationIdentifier = existingRecord?.destinationIdentifier;
      const mappedRecord = destinationIdentifier
        ? this._sourceRecords.find((sourceRecord) =>
            isSameRef(sourceRecord.ref, destinationIdentifier)
          )
        : undefined;

      return {
        label: { value: record.name },
        mapTo: {
          value: mappedRecord?.name || '',
          dataValidation: {
            type: 'list',
            formulae: [
              `"${this._sourceRecords
                .map((sourceRecord) => sourceRecord.name)
                .join(',')}"`,
            ],
          },
        },
      };
    });
  }
}

export abstract class XSLXToBasicRecord
  implements IXSLXImport<ISourceRecordToXSLX>
{
  abstract headers: AtLeast<Column, 'key' | 'header'>[];

  translate(row: Row): ISourceRecordToXSLX {
    return {
      label: row.getCell('label').value?.toString() ?? '',
      mapTo: row
        .getCell('mapTo')
        .value?.toString() as ISourceRecordToXSLX['mapTo'],
    };
  }
}
